import React from 'react'
import classNames from 'classnames'

import { SecretsModel } from '../../models/domain/secrets.model'
import { DomainSecretsContext } from './domain-secrets-context'
import { Well } from '@pushly/aqe/lib/components'
import { Flex, Form, Input, Radio, Space } from 'antd'
import type { RadioChangeEvent } from 'antd'
import { secretTypeLabels } from './domain-secret-labels'
import { DomainSecretType } from '@pushly/models/lib/enums/domain-secret-type'

type DomainSecretsEditorProps = {
    loading?: boolean
    secret: SecretsModel
    isEditMode: boolean
    domainId: number
    onChange?: (value: SecretsModel) => any
    className?: string
}

let CHANGE_DEBOUNCE_TIMEOUT: any

export const DomainSecretsEditor = (props: DomainSecretsEditorProps) => {
    const { domainId, secret: _secret, isEditMode, loading } = props

    const secret = _secret.clone()
    const editorClassName = classNames('domain-secrets-editor', props.className)
    const effectiveType = secret.type ?? DomainSecretType.TOKEN

    const emitChange = React.useCallback(
        async (changes: SecretsModel, debounce: boolean = false) => {
            clearTimeout(CHANGE_DEBOUNCE_TIMEOUT)

            if (props.onChange) {
                if (!debounce) {
                    props.onChange(changes)
                    return
                }

                CHANGE_DEBOUNCE_TIMEOUT = setTimeout(() => {
                    props.onChange!(changes)
                }, 320)
            }
        },
        [!props.onChange],
    ) // only modify callback if fn existence changes

    React.useEffect(() => {
        if (!secret.type) {
            emitChange(secret.clone({ type: DomainSecretType.TOKEN }))
        }
    }, [secret.type, emitChange])

    const getUserPassValue = () => {
        if (!secret.value) {
            return { username: '', password: '' }
        }

        try {
            const parsed = JSON.parse(secret.value)

            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return {
                    username: typeof parsed.username === 'string' ? parsed.username : '',
                    password: typeof parsed.password === 'string' ? parsed.password : '',
                }
            }
        } catch (e) {
            return { username: '', password: '' }
        }

        return { username: '', password: '' }
    }

    const handleAliasChange = (e) => {
        secret.alias = e.target.value
        emitChange(secret, false).then()
    }
    const handleSecretTypeChange = (event: RadioChangeEvent) => {
        secret.type = event.target.value as DomainSecretType
        secret.value = ''
        emitChange(secret, false).then()
    }
    const handleValueChange = (e) => {
        const { value } = e.target
        secret.value = value
        emitChange(secret, false).then()
    }

    const handleUserNameChange = (e) => {
        const userPassValue = getUserPassValue()
        userPassValue.username = e.target.value
        secret.value = JSON.stringify(userPassValue)
        emitChange(secret, false).then()
    }

    const handlePasswordChange = (e) => {
        const userPassValue = getUserPassValue()
        userPassValue.password = e.target.value
        secret.value = JSON.stringify(userPassValue)
        emitChange(secret, false).then()
    }

    const renderValueInput = () => {
        const type = secret.type ?? DomainSecretType.TOKEN

        switch (type) {
            case DomainSecretType.TOKEN:
                return (
                    <Input.Password
                        size="small"
                        value={secret.value ?? ''}
                        className="domain-secret-password"
                        autoComplete="off"
                        onChange={handleValueChange}
                    />
                )
            case DomainSecretType.JSON:
                return <Input.TextArea size="small" value={secret.value ?? ''} onChange={handleValueChange} />
            case DomainSecretType.USERPASS: {
                const { username, password } = getUserPassValue()
                return (
                    <Flex justify="stretch" gap={8}>
                        <Input size="small" placeholder="Username" value={username} onChange={handleUserNameChange} />
                        <Input.Password
                            size="small"
                            placeholder="Password"
                            value={password}
                            className="domain-secret-password"
                            autoComplete="on"
                            onChange={handlePasswordChange}
                        />
                    </Flex>
                )
            }
        }
    }

    return (
        <div className={editorClassName}>
            <DomainSecretsContext.Provider
                value={{
                    loading: loading ?? false,
                    domainId,
                    secret,
                    setSecret: emitChange,
                }}
            >
                <Well className="domain-secrets-well" title="Secret" hideFooter={true} variant="ghost" padding="none">
                    <Form.Item label="Alias">
                        <Input
                            size="small"
                            disabled={isEditMode}
                            value={secret.alias ?? ''}
                            onChange={handleAliasChange}
                        />
                    </Form.Item>

                    <Form.Item label="Secret Type" className="domain-secret-type">
                        <Radio.Group
                            size="small"
                            defaultValue={effectiveType}
                            disabled={isEditMode}
                            onChange={handleSecretTypeChange}
                            className="domain-secret-type-options"
                            buttonStyle="solid"
                        >
                            {Object.values(DomainSecretType).map((value) => (
                                <Radio.Button key={value} value={value}>
                                    {secretTypeLabels[value]}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="Value" className="domain-secret-value">
                        {renderValueInput()}
                    </Form.Item>
                </Well>
            </DomainSecretsContext.Provider>
        </div>
    )
}
